// 반복(행 스코프 조립) children input 속성 패널의 행 스코프 순수 로직(P4a).
// 반복 안 input은 bindingKey가 행 필드 상대키라 body 파생 축(writeFormFields dataType)이 전제부터
// 다르다 — 위젯 종류 후보와 행 필드 매핑 옵션을 여기서 산출한다(EditorPage가 소비, 판정은
// rc findEnclosingRepeatWidget). 결정: 2026-07-20-composer-repeat-widget-row-scope §자유 편집.

import type { CatalogEntry, DataSourceDef, InputWidget, WidgetType } from '@safetyhub/shared';
import {
  REGISTERED_OBJECT_FIELD_CONTRACTS,
  repeatSkeletonWidgetForField,
  type RegisteredObjectSubfield,
} from '@safetyhub/report-composer';
import { rowScopeChildOptions } from './rowScopeKeys';
import { declaredChildrenForDataSourceField } from './repeatSources';

/**
 * 행 스코프 위젯 종류 후보(기본 전 위젯) — body dataType 파생(WIDGET_OPTIONS_BY_DATATYPE) 대신
 * 자유 편집 어휘. 제외와 근거:
 *  - photo: 업로드 파이프라인·값 형태(사진 객체 배열)가 본문 스코프 전제 — 행 객체 필드로 미지원.
 *  - table: 내장 데이터셋(dataDefs) 편집 표 — dataType=array 전용 배치물이라 행 안에서 무의미.
 *  - dataSourceRows: 편집형 list 컨테이너 자체 — 반복 중첩(행 안 행 그리드)은 미지원
 *    (collectRepeatPlacedFieldKeys도 중첩 repeat 미하강).
 *  - aiTrigger: 예약만 된 미구현 슬롯 — 본문 후보에서도 숨김(2026-07-16 확정)과 동일.
 *  - registered: widgetKey 동반이 필수라 종류 select로 전환해 넣는 위젯이 아님 — 뼈대 생성
 *    (repeatSkeletonWidgetForField)과 현재값 보존으로만 표시.
 *  - signature: registered와 같은 이유 + 더 위험하다. 서명 슬롯은 **행 필드 선언**에서 나오고
 *    그 선언이 곧 슬롯 주소(slotKey)다 — 종류 select로 임의 input을 서명으로 바꾸면 선언 없는
 *    서명 칸이 생겨, 걷은 서명을 어디에 둘지 아무도 모르는 상태가 된다. 서명 칸은 데이터 소스
 *    필드 위젯 표에서만 지정한다(뼈대 생성·현재값 보존으로만 여기 나타난다).
 * textarea-list는 행 스코프 주 용도(개선대책류 string[])로 정식 포함(P0 §신규 투자 4).
 * text-list는 그 한 줄 판(짧은 항목 목록)으로 함께 포함.
 * image는 행 필드 값(URL)을 읽기 전용 이미지로 표시(구 SMS 이행상태 카드 사진 대응물) — 행 스코프 정식 포함.
 * annotated-image는 그 상위 호환(값이 객체면 사진 위 사각형·선 오버레이) — 같은 이유로 포함.
 * date-range(기간)도 포함한다 — 위 제외 사유(선언 쪽 배선이 있어야 성립하는 위젯)가 해당되지
 * 않는다: 값이 객체일 뿐 배선 없이 그 자리에서 완결되는 평범한 입력이고, 같은 축인 date가 이미
 * 후보라 기간만 빼면 "날짜는 고르는데 기간은 없는" 비대칭이 된다.
 * ⚠️ 본문 후보(WRITE_WIDGETS)에는 넣지 않는다 — 기간은 행 스코프에서 요청된 어휘다.
 */
export const REPEAT_ROW_WIDGETS: WidgetType[] = [
  'text',
  'textarea',
  'textarea-list',
  'text-list',
  'select',
  'radio',
  'checkbox',
  'date',
  'date-range',
  'time',
  'number',
  'image',
  'annotated-image',
  'sortableSelect',
  'combobox',
];

/** 행 스코프 위젯 종류 후보 + 현재값 보존(widgetOptionsForDataType의 current 보존 규약 동형). */
export function widgetOptionsForRepeatRow(current: WidgetType): WidgetType[] {
  return REPEAT_ROW_WIDGETS.includes(current)
    ? REPEAT_ROW_WIDGETS
    : [current, ...REPEAT_ROW_WIDGETS];
}

/** 행 필드 매핑 select 옵션 1건 — preserved=현재값이 def.fields 선언 밖(보존 표기). */
export interface RepeatRowBindingOption {
  value: string;
  label: string;
  preserved?: boolean;
}

/**
 * 계약(등록형 객체 필드) 자식 해소 — bindingKey `<부모필드>.<자식경로>`가 def의 계약 자식과
 * 일치하면 그 사전 항목(옵션 소스·숫자 여부·라벨) 반환. 비계약·단일 세그먼트면 undefined.
 * 자식 선택 시 자동 배선의 정본(일반 위젯 패널·formTable 셀 카드 공유 — 2026-08-05 승격).
 * 카탈로그 **선언** 자식(riskTypeFlags.* 등)은 계약이 아니라 여기서 undefined — 자동 배선할 사전이
 * 없고, 위젯은 저작자가 고른 것(checkbox 등)이 그대로다.
 */
export function resolveRepeatContractSub(
  def: DataSourceDef,
  key: string,
): RegisteredObjectSubfield | undefined {
  const dot = key.indexOf('.');
  if (dot <= 0) return undefined;
  const parentKey = key.slice(0, dot);
  const childPath = key.slice(dot + 1);
  const parent = def.fields.find((f) => f.field === parentKey);
  const subs = parent?.widgetKey ? REGISTERED_OBJECT_FIELD_CONTRACTS[parent.widgetKey] : undefined;
  return subs?.find((s) => s.path === childPath);
}

/**
 * optionsFrom이 어떤 계약 항목과 정확 일치(자동 배선 흔적)인지 — 계약 자식 → 비계약 전환 시
 * 이 흔적만 제거(수동 커스텀 설정은 정확 일치 아닐 때 무접촉).
 */
export function isAutoWiredContractOptionsFrom(of?: {
  catalogKey: string;
  valueField: string;
  labelField: string;
}): boolean {
  return (
    !!of &&
    Object.values(REGISTERED_OBJECT_FIELD_CONTRACTS).some((subs) =>
      subs.some(
        (s) =>
          // 선택지 없는 계약 자식(improvement-image 등)은 자동 배선 흔적을 남기지 않는다.
          !!s.optionsFrom &&
          s.optionsFrom.catalogKey === of.catalogKey &&
          s.optionsFrom.valueField === of.valueField &&
          s.optionsFrom.labelField === of.labelField,
      ),
    )
  );
}

/**
 * 반복 children input의 행 필드(bindingKey) 후보 — 소속 repeat over 데이터소스의 def.fields 전체
 * (hidden 포함 — 자유 편집이라 숨김 선언 필드로의 매핑도 허용). 자식이 있는 필드는 그 옵션
 * **바로 뒤에 자식 옵션**(`<필드>.<자식경로>`)을 직접 노출한다 — 자식 출처는 둘이다:
 *  - 계약(등록형 객체 필드, REGISTERED_OBJECT_FIELD_CONTRACTS[widgetKey]) — 저작자가 자식
 *    (빈도/강도/수준)을 select에서 바로 고르면 onChange가 옵션 소스·숫자 기록을 자동 배선한다
 *    (2026-07-21 사용자 확정, 배관 은닉).
 *  - 카탈로그 선언(`returns[].fields` — `catalog` 인자로 해소, 미전달이면 이 축은 비어 있다) —
 *    위험성평가 `riskTypeFlags.<키>` 13칸이 실전. 종전엔 이 축이 없어 widgetKey 없는 객체 필드의
 *    자식을 이 select로는 처음부터 만들 수 없었다(2026-09-14 은닉 감사 D-3).
 * 현재값이 선언(필드·자식) 밖이면 보존 옵션을 선두에 유지(재저장 시 소실 방지 — sortableSelect
 * 소스 보존 규약 동형).
 */
export function repeatRowBindingOptions(
  def: DataSourceDef,
  current: string,
  catalog?: CatalogEntry[],
): RepeatRowBindingOption[] {
  const opts: RepeatRowBindingOption[] = [];
  for (const f of def.fields) {
    opts.push({ value: f.field, label: f.label ? `${f.label} (${f.field})` : f.field });
    // 자식 파생은 rowScopeKeys가 정본(계약 ∪ 선언) — 본문 표시 키 피커(rowFieldPickerOptions)와
    // 같은 라벨·경로를 쓴다. 사본으로 두면 두 표면의 자식 후보가 조용히 갈린다.
    const children = declaredChildrenForDataSourceField(def, f, catalog);
    for (const child of rowScopeChildOptions({
      key: f.field,
      label: f.label,
      widgetKey: f.widgetKey,
      ...(children.length ? { children } : {}),
    })) {
      opts.push({ value: child.key, label: child.label });
    }
  }
  if (!opts.some((o) => o.value === current)) {
    // 현재값이 선언(필드·계약 자식) 밖 — 점 경로(중첩 저장, 예 riskEstimation.custom)는 선언된 객체
    // 필드의 **자식 필드**로 표기(부모 라벨 병기), 부모도 선언 밖이면 기존 보존 표기 유지.
    const dot = current.indexOf('.');
    if (dot > 0) {
      const parentKey = current.slice(0, dot);
      const childPath = current.slice(dot + 1);
      const parent = def.fields.find((f) => f.field === parentKey);
      if (parent) {
        const parentLabel = parent.label ? `${parent.label}(${parentKey})` : parentKey;
        return [
          { value: current, label: `${parentLabel}의 자식 필드 › ${childPath}`, preserved: true },
          ...opts,
        ];
      }
    }
    return [{ value: current, label: `${current} (필드 선언 밖 — 보존)`, preserved: true }, ...opts];
  }
  return opts;
}

/**
 * 반복 소스 행 필드 옵션 1건 → 셀 배치용 InputWidget 생성(formTable 고정 셀 「행 필드에서 배치」
 * select의 onChange 정본 — 2026-08-05 합성, 2026-09-14 EditorPage에서 이 모듈로 승격).
 *
 * `repeatRowBindingOptions`가 노출하는 옵션 **전부**를 여기서 해소해야 한다 — 옵션에는 있는데
 * 여기서 null이면 select가 「선택해도 아무 일도 안 하는」 상태가 된다(은닉 감사 2차 ①). 세 갈래:
 *  - 계약 자식(`부모.자식`, REGISTERED_OBJECT_FIELD_CONTRACTS): select + 계약 사전(optionsFrom·
 *    dataType·라벨) 자동 배선(일반 위젯 패널의 자식 선택 배선과 동일 규칙). 선택지 없는 계약 자식
 *    (improvement-image url 등 — 부모 위젯이 값을 쓴다)은 읽기 전용 표시 입력.
 *  - 카탈로그 **선언** 자식(`부모.자식`, `declaredChildrenForDataSourceField` — 위험성평가
 *    `riskTypeFlags.fall` 등 13칸): 자동 배선할 사전이 없으므로 기본 text 입력(라벨=자식 라벨,
 *    treeOwned). 위젯 종류(checkbox 등)는 저작자가 셀 카드에서 고른다 — `resolveRepeatContractSub`
 *    주석의 「위젯은 저작자가 고른 것이 그대로다」와 같은 규약. 계약 자식이 같은 키를 이미 이겼으면
 *    (rowScopeChildOptions) 위 갈래가 먼저 잡으므로 여기엔 오지 않는다.
 *  - 일반 필드(정확 일치): repeatSkeletonWidgetForField(뼈대 매핑 정본 — readonly·widgetKey 승계).
 * null은 진짜 미해소뿐이다: 결과가 input이 아닌 조립형(risk-estimation=group — 셀 위젯 슬롯은
 * InputWidget 전용) · hidden 필드 · 선언에 없는 키.
 */
export function cellWidgetFromRepeatOption(
  def: DataSourceDef,
  optionValue: string,
  catalog: CatalogEntry[] | undefined,
  newId: () => string,
): InputWidget | null {
  const sub = resolveRepeatContractSub(def, optionValue);
  if (sub) {
    // 선택지 없는 계약 자식(improvement-image url 등 — 부모 위젯이 값을 쓴다)은 읽기 전용
    // 표시 입력(inputWidget 힌트, 기본 text)으로 굽는다 — select에 빈 optionsFrom을 얹으면
    // 옵션 없는 드롭다운이 되어 저작자가 원인을 알 수 없다.
    return {
      id: newId(),
      kind: 'input',
      bindingKey: optionValue,
      widget: sub.optionsFrom ? 'select' : (sub.inputWidget ?? 'text'),
      label: sub.label,
      treeOwned: true,
      ...(sub.optionsFrom ? { optionsFrom: { ...sub.optionsFrom } } : { mode: 'readonly' as const }),
      ...(sub.dataType ? { dataType: sub.dataType } : {}),
    };
  }
  const dot = optionValue.indexOf('.');
  if (dot > 0) {
    const parent = def.fields.find((f) => f.field === optionValue.slice(0, dot));
    const childPath = optionValue.slice(dot + 1);
    const declared = parent
      ? declaredChildrenForDataSourceField(def, parent, catalog).find((c) => c.path === childPath)
      : undefined;
    if (declared) {
      return {
        id: newId(),
        kind: 'input',
        bindingKey: optionValue,
        widget: 'text',
        label: declared.label,
        treeOwned: true,
      };
    }
  }
  const field = def.fields.find((f) => f.field === optionValue);
  if (!field) return null;
  const skeleton = repeatSkeletonWidgetForField(field, newId);
  return skeleton && skeleton.kind === 'input' ? skeleton : null;
}
